/**
 * markdown.js — tiny, dependency-free, **escape-first** Markdown renderer.
 *
 * Used by <g-notes> to render note bodies. The single most important property
 * is XSS safety: the ENTIRE input is HTML-escaped BEFORE any transform, so no
 * user byte ever reaches innerHTML unescaped — `<script>`, `<img onerror>`,
 * `<svg>` all become inert text. We then emit only a fixed, closed set of tags
 * whose attributes are renderer-controlled (never copied from input). There is
 * no raw-HTML passthrough. Link and image URLs are validated against a scheme
 * allow-list.
 *
 * This is deliberately NOT CommonMark-complete — notes are short; unknown
 * syntax degrades to escaped text, never raw HTML. Supports: ATX headings,
 * **bold** / *italic*, ~~strikethrough~~, `inline code`, ```fenced code```,
 * - / * / + and 1. lists, - [ ] task lists, > blockquotes, [links](url),
 * <https://autolinks>, ![images](url), GFM pipe tables, --- horizontal rules,
 * and paragraphs with hard line breaks.
 *
 * The construct set is kept in step with <g-editor>, which is what usually
 * produces the markdown on the other end. ./html-to-markdown.js is the
 * inverse and covers the same list — <g-notes> edits the rendered HTML in a
 * contenteditable and serializes it straight back, so a construct this file
 * renders but that file cannot read is a construct that gets destroyed by an
 * edit. Add to both or neither.
 */

export function renderMarkdown(md) {
  if (typeof md !== 'string' || md === '') return '';

  // 1. Escape EVERYTHING first — the core safety guarantee.
  const escaped = escapeHtml(md.replace(/\r\n?/g, '\n'));
  const lines = escaped.split('\n');

  const out = [];
  let para = [];
  let listType = null; // 'ul' | 'ol'
  let i = 0;

  const flushPara = () => {
    if (para.length) { out.push(`<p>${inline(para.join('<br>'))}</p>`); para = []; }
  };
  const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block ``` ... ```
    if (/^\s*```/.test(line)) {
      flushPara(); closeList();
      const code = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { code.push(lines[i]); i++; }
      i++; // skip closing fence (or run off EOF)
      out.push(`<pre class="bg-base-200 rounded p-2 overflow-x-auto text-xs my-1"><code>${code.join('\n')}</code></pre>`);
      continue;
    }

    // Blank line
    if (/^\s*$/.test(line)) { flushPara(); closeList(); i++; continue; }

    // ATX heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushPara(); closeList();
      const level = h[1].length;
      out.push(`<h${level} class="font-semibold ${headingSize(level)}">${inline(h[2])}</h${level}>`);
      i++; continue;
    }

    // Thematic break — checked before lists, since `- - -` and `***` would
    // otherwise read as a bullet or as emphasis.
    if (/^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/.test(line)) {
      flushPara(); closeList();
      out.push('<hr class="border-base-300 my-2">');
      i++; continue;
    }

    // GFM pipe table: a header row, then a delimiter row that fixes the column
    // count and alignment, then body rows until the first line that isn't one.
    const aligns = i + 1 < lines.length ? delimiterRow(lines[i + 1]) : null;
    if (aligns && line.includes('|')) {
      const head = splitRow(line);
      if (head.length === aligns.length) {
        flushPara(); closeList();
        i += 2;
        const rows = [];
        while (i < lines.length && lines[i].includes('|') && !/^\s*$/.test(lines[i])) {
          rows.push(splitRow(lines[i]));
          i++;
        }
        out.push(renderTable(head, aligns, rows));
        continue;
      }
    }

    // Blockquote ('>' was escaped to '&gt;')
    if (/^&gt;\s?/.test(line)) {
      flushPara(); closeList();
      const quote = [];
      while (i < lines.length && /^&gt;\s?/.test(lines[i])) { quote.push(lines[i].replace(/^&gt;\s?/, '')); i++; }
      out.push(`<blockquote class="border-l-2 border-base-300 pl-3 my-1 opacity-80">${inline(quote.join('<br>'))}</blockquote>`);
      continue;
    }

    // Unordered list item, which may be a task item
    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    if (ul) {
      flushPara();
      if (listType !== 'ul') { closeList(); out.push('<ul class="list-disc ml-5 my-1">'); listType = 'ul'; }
      out.push(listItem(ul[1]));
      i++; continue;
    }

    // Ordered list item
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ol) {
      flushPara();
      if (listType !== 'ol') { closeList(); out.push('<ol class="list-decimal ml-5 my-1">'); listType = 'ol'; }
      out.push(listItem(ol[1]));
      i++; continue;
    }

    // Paragraph line
    closeList();
    para.push(line);
    i++;
  }
  flushPara(); closeList();
  return out.join('\n');
}

function headingSize(level) {
  return level <= 1 ? 'text-base' : 'text-sm';
}

// `- [ ] thing` / `- [x] thing`. The box is disabled: a note body is read-only
// until you click into it, and a live checkbox would swallow that click.
function listItem(content) {
  const task = content.match(/^\[([ xX])\]\s+(.*)$/);
  if (!task) return `<li>${inline(content)}</li>`;
  const checked = task[1] !== ' ' ? ' checked' : '';
  return `<li class="list-none -ml-5 flex items-start gap-2">`
    + `<input type="checkbox" class="checkbox checkbox-xs mt-0.5" disabled${checked}>`
    + `<span>${inline(task[2])}</span></li>`;
}

// ── Tables ──

// Recognise `| --- | :-: | ---: |`, returning one alignment per column (null
// where none was given), or null when the line is not a delimiter row at all.
function delimiterRow(line) {
  if (!line || !line.includes('-')) return null;
  const cells = splitRow(line);
  if (!cells.length) return null;
  const aligns = [];
  for (const cell of cells) {
    const m = cell.match(/^(:?)-+(:?)$/);
    if (!m) return null;
    if (m[1] && m[2]) aligns.push('center');
    else if (m[2]) aligns.push('right');
    else if (m[1]) aligns.push('left');
    else aligns.push(null);
  }
  return aligns;
}

// Split a table row on its unescaped pipes. The outer pipes are optional in
// GFM, so one leading and one trailing pipe are dropped before splitting.
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);

  const cells = [];
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\\' && s[i + 1] === '|') { cur += '|'; i++; continue; }
    if (s[i] === '|') { cells.push(cur.trim()); cur = ''; continue; }
    cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

function renderTable(head, aligns, rows) {
  const align = (a) => (a ? ` class="text-${a}"` : '');
  const th = head.map((c, n) => `<th${align(aligns[n])}>${inline(c)}</th>`).join('');
  // Body rows are forced to the header's width: GFM drops the overflow and
  // pads the shortfall, rather than producing a ragged table.
  const tb = rows.map((r) => '<tr>'
    + aligns.map((a, n) => `<td${align(a)}>${inline(r[n] || '')}</td>`).join('')
    + '</tr>').join('');
  return '<div class="overflow-x-auto my-1"><table class="table table-xs">'
    + `<thead><tr>${th}</tr></thead><tbody>${tb}</tbody></table></div>`;
}

// ── Inline ──

// Inline transforms run on ALREADY-ESCAPED text. We tokenize on inline-code
// spans (backtick isn't escaped and isn't a regex metachar) and only transform
// the non-code segments — no sentinel, so plain numbers/punctuation are safe.
function inline(text) {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((seg) => {
    if (seg.length >= 2 && seg.startsWith('`') && seg.endsWith('`')) {
      return `<code class="px-1 py-0.5 rounded bg-base-200 text-xs font-mono">${seg.slice(1, -1)}</code>`;
    }
    return linked(seg);
  }).join('');
}

// ![alt](src), [label](url) and <https://autolinks>. Consumed BEFORE emphasis
// so a URL never reaches those passes: `http://x.com/a_b_c` would otherwise
// come out italicised, and the emitted href= would be a target for the next
// regex in line.
const LINK_RE = /(!?)\[([^\]]*)\]\(([^)\s]+)\)|&lt;((?:https?:\/\/|mailto:)[^\s&]+)&gt;/g;

function linked(seg) {
  let out = '';
  let last = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(seg)) !== null) {
    out += emphasis(seg.slice(last, m.index));
    if (m[4] !== undefined) out += anchor(m[4], m[4]);
    else if (m[1] === '!') out += image(m[2], m[3]);
    else out += anchor(m[3], emphasis(m[2]));
    last = LINK_RE.lastIndex;
  }
  return out + emphasis(seg.slice(last));
}

function anchor(url, label) {
  const safe = safeUrl(url);
  if (!safe) return label; // drop the link, keep the visible text
  return `<a href="${safe}" rel="noopener noreferrer" target="_blank" class="link link-primary">${label}</a>`;
}

function image(alt, src) {
  const safe = safeUrl(src);
  if (!safe) return alt; // no URL we trust — the alt text is the fallback
  return `<img src="${safe}" alt="${alt}" class="max-w-full h-auto rounded my-1" loading="lazy">`;
}

function emphasis(t) {
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  t = t.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  t = t.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  t = t.replace(/(^|[\s(])_([^_]+)_(?=$|[\s).,!?])/g, '$1<em>$2</em>');
  return t;
}

/**
 * Accept only safe schemes.
 *
 * Exported so every component that puts data into an `href` shares ONE
 * allow-list — a second copy is a second thing to forget the day a new scheme
 * needs rejecting.
 *
 * The input is already HTML-escaped (so `"` is `&quot;` and cannot break out of
 * the href attribute); this additionally rejects javascript:/data:/vbscript:
 * and anything else, by allow-listing rather than blocklisting.
 */
export function safeUrl(url) {
  const u = url.trim();
  if (/^(\/|#|\.\/|\.\.\/)/.test(u)) return u;      // relative / anchor
  if (/^mailto:[^\s]+$/i.test(u)) return u;          // mailto
  if (/^https?:\/\/[^\s]+$/i.test(u)) return u;      // http(s)
  return null;                                        // reject everything else
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}
